import React from 'react';
import { motion } from 'framer-motion';
import { ProgressIndicator } from './ProgressIndicator';
interface SlideFeedbackProps {
  onNext: () => void;
  pollAnswer: number | null;
}
function DistributionBar({
  label,
  percentage,
  delay,
  highlight
}: {
  label: string;
  percentage: number;
  delay: number;
  highlight?: boolean;
}) {
  return <div className="space-y-1.5" data-id="element-89">
      <div className="flex justify-between text-sm" data-id="element-90">
        <span className={highlight ? 'text-teal-600 font-medium' : 'text-slate-500'} data-id="element-91">
          {label}
        </span>
        <span className={highlight ? 'text-teal-600 font-semibold' : 'text-slate-600'} data-id="element-92">
          {percentage}%
        </span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-93">
        <motion.div className={`h-full rounded-full ${highlight ? 'bg-teal-500' : 'bg-slate-400'}`} initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: 1,
        delay,
        ease: 'easeOut'
      }} style={{
        width: `${percentage}%`,
        transformOrigin: 'left'
      }} data-id="element-94" />
      </div>
    </div>;
}
export function SlideFeedback({
  onNext,
  pollAnswer
}: SlideFeedbackProps) {
  const distributions = [27, 19, 54];
  const matchPercentage = pollAnswer !== null ? distributions[pollAnswer] : 73;
  return <div className="relative w-full h-full flex flex-col items-center justify-center px-6" data-id="element-95">
      <div className="relative z-10 max-w-2xl mx-auto w-full flex flex-col items-center" data-id="element-96">
        <motion.h2 className="text-3xl sm:text-4xl md:text-[2.6rem] font-bold text-slate-900 text-center leading-snug mb-6" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} data-id="element-97">
          Across Europe, clinicians are looking{' '}
          <span className="text-teal-600" data-id="element-98">beyond first impressions</span>
        </motion.h2>

        <motion.p className="text-base sm:text-lg text-slate-500 text-center max-w-xl mb-8 leading-relaxed" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.3
      }} data-id="element-99">
          Long-term real-world evidence is becoming essential to understand how
          prophylaxis performs in everyday clinical practice — across patients
          and over time.
        </motion.p>

        {/* Distribution bars */}
        <motion.div className="w-full max-w-md space-y-4 mb-8 bg-slate-50 rounded-xl p-6 border border-slate-200" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} data-id="element-100">
          <DistributionBar label="Convenience" percentage={distributions[0]} delay={0.9} highlight={pollAnswer === 0} data-id="element-101" />
          <DistributionBar label="Speed of response" percentage={distributions[1]} delay={1.1} highlight={pollAnswer === 1} data-id="element-102" />
          <DistributionBar label="Long-term control" percentage={distributions[2]} delay={1.3} highlight={pollAnswer === 2} data-id="element-103" />
        </motion.div>

        {/* "X% answered like you" */}
        <motion.p className="text-sm text-teal-600 mb-10 font-medium" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.8,
        duration: 0.8
      }} data-id="element-104">
          {matchPercentage}% of respondents answered like you
        </motion.p>

        <motion.button onClick={onNext} className="px-8 py-3.5 bg-takeda-red text-white font-semibold rounded-lg text-base
                     hover:bg-takeda-darkred transition-all duration-300
                     shadow-lg shadow-red-500/20 
                     focus:outline-none focus:ring-2 focus:ring-takeda-red focus:ring-offset-2 focus:ring-offset-white" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 1.4
      }} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.98
      }} aria-label="See the data — proceed to next slide" data-id="element-105">
          See the data
        </motion.button>

        <motion.div className="mt-10" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.6,
        duration: 0.6
      }} data-id="element-106">
          <ProgressIndicator currentStep={3} totalSteps={7} data-id="element-107" />
        </motion.div>
      </div>
    </div>;
}